<template>
  <div class="summary-section">

    <!-- Skeleton (carregamento inicial) -->
    <template v-if="loading">
      <div class="summary-hero">
        <div class="hero-left">
          <SkeletonBlock width="220px" height="24px" />
          <div class="hero-kpis">
            <SkeletonBlock v-for="i in 4" :key="i" width="60px" height="34px" />
          </div>
        </div>
        <SkeletonBlock width="120px" height="120px" radius="50%" />
      </div>
      <div class="widgets-grid">
        <div v-for="i in 5" :key="i" class="widget widget--skeleton">
          <SkeletonBlock width="60%" height="14px" />
          <SkeletonBlock width="100%" height="12px" />
          <SkeletonBlock width="80%" height="12px" />
        </div>
      </div>
    </template>

    <template v-else>
    <!-- Hero -->
    <div class="summary-hero">
      <div class="hero-left">
        <div class="hero-title-row">
          <h2 class="hero-title">{{ projectName }}</h2>
          <span :class="['status-pill', delayedTasks.length > 0 ? 'status-late' : 'status-ok']">
            {{ delayedTasks.length > 0 ? `${delayedTasks.length} atrasada${delayedTasks.length > 1 ? 's' : ''}` : 'Em dia' }}
          </span>
        </div>

        <div class="hero-kpis">
          <div class="kpi">
            <span class="kpi-value">{{ ganttRows.length }}</span>
            <span class="kpi-label">Tarefas</span>
          </div>
          <div class="kpi-divider"></div>
          <div class="kpi">
            <span class="kpi-value">{{ todayTasks.length }}</span>
            <span class="kpi-label">Hoje</span>
          </div>
          <div class="kpi-divider"></div>
          <div class="kpi">
            <span class="kpi-value">{{ openTickets.length }}</span>
            <span class="kpi-label">Chamados</span>
          </div>
          <div class="kpi-divider"></div>
          <div class="kpi">
            <span class="kpi-value">{{ projectReports.length }}</span>
            <span class="kpi-label">Relatórios</span>
          </div>
        </div>
      </div>

      <!-- Progress donut -->
      <div class="hero-donut">
        <svg class="donut-svg" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(0,0,0,0.07)" stroke-width="12"/>
          <circle
            cx="60" cy="60" r="48"
            fill="none"
            :stroke="progress >= 100 ? '#16a34a' : progress > 60 ? 'var(--accent,#0f4c81)' : progress > 30 ? '#f59e0b' : '#ef4444'"
            stroke-width="12"
            stroke-linecap="round"
            :stroke-dasharray="`${(progress * 301.59 / 100).toFixed(2)} 301.59`"
            transform="rotate(-90 60 60)"
            class="donut-arc"
          />
          <text x="60" y="55" text-anchor="middle" class="donut-pct" font-size="22" font-weight="700" fill="var(--ink,#201f1e)">{{ progress }}%</text>
          <text x="60" y="72" text-anchor="middle" font-size="10" fill="var(--ink-muted,#605e5c)">progresso</text>
        </svg>
      </div>
    </div>

    <!-- Widgets grid -->
    <div class="widgets-grid">

      <!-- Tasks today -->
      <div class="widget" @click="$emit('navigate', 'gantt')" role="button" tabindex="0">
        <div class="widget-head">
          <div class="widget-icon widget-icon--blue">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <span class="widget-title">Tarefas de hoje</span>
          <span class="widget-link">Ver planejamento →</span>
        </div>
        <div v-if="todayTasks.length === 0" class="widget-empty">Nenhuma tarefa para hoje.</div>
        <ul v-else class="widget-list">
          <li v-for="task in todayTasks.slice(0, 4)" :key="task.id" class="widget-task-item">
            <div class="task-item-bar">
              <div class="task-item-fill" :style="{ width: taskPct(task) + '%' }"></div>
            </div>
            <div class="task-item-info">
              <span class="task-item-name">{{ task.title }}</span>
              <span class="task-item-meta">{{ resolveUser(task.assigneeUsername) }} · {{ taskPct(task) }}%</span>
            </div>
          </li>
        </ul>
        <div v-if="todayTasks.length > 4" class="widget-more">+{{ todayTasks.length - 4 }} tarefa(s)</div>
      </div>

      <!-- Overdue tasks -->
      <div class="widget" :class="{ 'widget--warn': delayedTasks.length > 0 }" @click="$emit('navigate', 'gantt')" role="button" tabindex="0">
        <div class="widget-head">
          <div class="widget-icon" :class="delayedTasks.length > 0 ? 'widget-icon--red' : 'widget-icon--green'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <span class="widget-title">Tarefas atrasadas</span>
          <span v-if="delayedTasks.length > 0" class="widget-badge-count">{{ delayedTasks.length }}</span>
        </div>
        <div v-if="delayedTasks.length === 0" class="widget-empty widget-empty--ok">Todas em dia ✓</div>
        <ul v-else class="widget-list">
          <li v-for="task in delayedTasks.slice(0, 3)" :key="task.id" class="widget-item late-item">
            <span class="task-item-name">{{ task.title }}</span>
            <span class="late-date">venceu {{ formatDate(task.plannedEnd) }}</span>
          </li>
        </ul>
        <div v-if="delayedTasks.length > 3" class="widget-more">+{{ delayedTasks.length - 3 }} tarefa(s)</div>
      </div>

      <!-- Open tickets -->
      <div class="widget" @click="$emit('navigate', 'tickets')" role="button" tabindex="0">
        <div class="widget-head">
          <div class="widget-icon widget-icon--orange">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </div>
          <span class="widget-title">Chamados abertos</span>
          <span class="widget-link">Ver todos →</span>
        </div>
        <div v-if="openTickets.length === 0" class="widget-empty">Nenhum chamado aberto.</div>
        <ul v-else class="widget-list">
          <li v-for="t in openTickets.slice(0, 3)" :key="t.id" class="widget-item">
            <span class="task-item-name">{{ t.title }}</span>
            <span :class="['ticket-prio', `ticket-prio--${t.priority}`]">{{ t.priority }}</span>
          </li>
        </ul>
        <div v-if="openTickets.length > 3" class="widget-more">+{{ openTickets.length - 3 }} chamado(s)</div>
      </div>

      <!-- Reports -->
      <div class="widget" @click="$emit('navigate', 'reports')" role="button" tabindex="0">
        <div class="widget-head">
          <div class="widget-icon widget-icon--teal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <span class="widget-title">Relatórios</span>
          <span class="widget-link">Ver todos →</span>
        </div>
        <div v-if="projectReports.length === 0" class="widget-empty">Nenhum relatório.</div>
        <ul v-else class="widget-list">
          <li v-for="r in projectReports.slice(0, 3)" :key="r.id" class="widget-item">
            <span class="task-item-name">{{ r.title }}</span>
            <span class="task-item-meta">{{ formatDate(r.reportDate) }}</span>
          </li>
        </ul>
        <div v-if="projectReports.length > 3" class="widget-more">+{{ projectReports.length - 3 }} relatório(s)</div>
      </div>

      <!-- Warehouse reservations -->
      <div class="widget" @click="$emit('navigate', 'warehouse')" role="button" tabindex="0">
        <div class="widget-head">
          <div class="widget-icon widget-icon--purple">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
            </svg>
          </div>
          <span class="widget-title">Almoxarifado</span>
          <span class="widget-link">Ver reservas →</span>
        </div>
        <div v-if="projectReservations.length === 0" class="widget-empty">Sem reservas ativas.</div>
        <ul v-else class="widget-list">
          <li v-for="res in projectReservations.slice(0, 3)" :key="res.id" class="widget-item">
            <span class="task-item-name">{{ resolveItem(res.itemId) }}</span>
            <span class="task-item-meta">×{{ res.quantity }}</span>
          </li>
        </ul>
        <div v-if="projectReservations.length > 3" class="widget-more">+{{ projectReservations.length - 3 }} reserva(s)</div>
      </div>

    </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import SkeletonBlock from '@/components/skeleton/SkeletonBlock.vue'

interface ProjectTask {
  id: string
  title: string
  assigneeUsername: string
  plannedStart: string
  plannedEnd: string
  progressUpdates?: Array<{ progressPercent?: number }>
}

export default defineComponent({
  name: 'ProjectSummarySection',
  components: { SkeletonBlock },
  props: {
    loading: { type: Boolean, default: false },
    projectName: { type: String, default: '' },
    progress: { type: Number, default: 0 },
    ganttRows: { type: Array as PropType<ProjectTask[]>, default: () => [] },
    projectReports: { type: Array as PropType<Array<{ id: string; title: string; reportDate: string }>>  , default: () => [] },
    projectReservations: { type: Array as PropType<Array<{ id: string; itemId: string; quantity: number }>>, default: () => [] },
    projectTickets: { type: Array as PropType<Array<{ id: string; title: string; priority: string; status: string }>>, default: () => [] },
    assignableUsers: { type: Array as PropType<Array<{ username: string; name: string }>>, default: () => [] },
    warehouseItems: { type: Array as PropType<Array<{ id: string; name: string }>>, default: () => [] }
  },
  emits: ['navigate'],
  setup(props) {
    const today = new Date().toISOString().split('T')[0]

    const todayTasks = computed(() =>
      props.ganttRows.filter(t => t.plannedStart <= today && t.plannedEnd >= today)
    )

    const delayedTasks = computed(() =>
      props.ganttRows.filter(t => {
        if (!t.plannedEnd || t.plannedEnd >= today) return false
        const updates = t.progressUpdates || []
        const last = updates[updates.length - 1]
        return (last?.progressPercent ?? 0) < 100
      })
    )

    const openTickets = computed(() =>
      props.projectTickets.filter(t => t.status !== 'resolvido')
    )

    const taskPct = (task: ProjectTask) => {
      const updates = task.progressUpdates || []
      if (!updates.length) return 0
      return Math.round(Number(updates[updates.length - 1].progressPercent ?? 0))
    }

    const resolveUser = (username: string) =>
      props.assignableUsers.find(u => u.username === username)?.name || username

    const resolveItem = (itemId: string) =>
      props.warehouseItems.find(i => i.id === itemId)?.name || itemId

    const formatDate = (value: string) => {
      if (!value) return '-'
      const [y, m, d] = value.split('-')
      return `${d}/${m}/${y}`
    }

    return { todayTasks, delayedTasks, openTickets, taskPct, resolveUser, resolveItem, formatDate }
  }
})
</script>

<style scoped lang="scss">
.summary-section {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── HERO ── */
.summary-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  background: var(--card, #fff);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 14px;
  padding: 28px 32px;
}

.hero-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.hero-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--ink, #201f1e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  flex-shrink: 0;

  &.status-ok { background: #dcfce7; color: #15803d; }
  &.status-late { background: #fee2e2; color: #b91c1c; }
}

.hero-kpis {
  display: flex;
  align-items: center;
  gap: 0;
}

.kpi {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;
  &:first-child { padding-left: 0; }
}

.kpi-divider {
  width: 1px;
  height: 36px;
  background: rgba(0,0,0,0.1);
}

.kpi-value {
  font-size: 30px;
  font-weight: 700;
  color: var(--ink, #201f1e);
  line-height: 1;
}

.kpi-label {
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ── DONUT ── */
.hero-donut {
  flex-shrink: 0;
}

.donut-svg {
  width: 120px;
  height: 120px;
}

.donut-arc {
  transition: stroke-dasharray 0.5s ease;
}

/* ── WIDGETS GRID ── */
.widgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.widget--skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget {
  background: var(--card, #fff);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    border-color: rgba(0,0,0,0.14);
  }

  &--warn {
    border-color: rgba(239,68,68,0.25);
    background: #fff9f9;
  }
}

.widget-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.widget-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--blue { background: #dbeafe; color: #1d4ed8; }
  &--red { background: #fee2e2; color: #b91c1c; }
  &--green { background: #dcfce7; color: #15803d; }
  &--orange { background: #ffedd5; color: #c2410c; }
  &--teal { background: #ccfbf1; color: #0f766e; }
  &--purple { background: #ede9fe; color: #7c3aed; }
}

.widget-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink, #201f1e);
  flex: 1;
}

.widget-link {
  font-size: 11px;
  color: var(--accent, #0f4c81);
  font-weight: 600;
  flex-shrink: 0;
}

.widget-badge-count {
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 20px;
  flex-shrink: 0;
}

.widget-empty {
  font-size: 12px;
  color: var(--ink-muted, #605e5c);

  &--ok { color: #16a34a; font-weight: 600; }
}

.widget-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.widget-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.widget-task-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-item-bar {
  height: 4px;
  background: rgba(0,0,0,0.07);
  border-radius: 2px;
  overflow: hidden;
}

.task-item-fill {
  height: 100%;
  background: var(--accent, #0f4c81);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.task-item-info {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.task-item-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--ink, #201f1e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.task-item-meta {
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  flex-shrink: 0;
}

.late-item {
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
}

.late-date {
  font-size: 11px;
  color: #b91c1c;
  font-weight: 500;
}

.ticket-prio {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  text-transform: capitalize;
  flex-shrink: 0;

  &--alta { background: #fee2e2; color: #b91c1c; }
  &--media { background: #ffedd5; color: #c2410c; }
  &--baixa { background: #dcfce7; color: #15803d; }
}

.widget-more {
  margin-top: 8px;
  font-size: 11px;
  color: var(--ink-muted);
  font-style: italic;
}

@media (max-width: 900px) {
  .summary-section {
    padding: 16px;
    gap: 16px;
  }

  .summary-hero {
    flex-direction: column;
    padding: 18px 16px;
    gap: 16px;
  }

  .hero-donut {
    display: none;
  }

  .hero-title {
    font-size: 18px;
  }

  .hero-kpis {
    flex-wrap: wrap;
    gap: 12px 0;
  }

  .kpi {
    padding: 0 14px;
    flex: 1 1 auto;
    min-width: 60px;
    align-items: flex-start;
  }

  .kpi:first-child {
    padding-left: 0;
  }

  .kpi-value {
    font-size: 22px;
  }

  .kpi-divider {
    display: none;
  }

  .widgets-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
